import React from "react";
import { Box, useMediaQuery, GlobalStyles } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import celebratingSvg from "./attendant-celebrating.svg?raw";
import errorSvg from "./attendant-error.svg?raw";
import idleSvg from "./attendant-idle.svg?raw";
import listeningSvg from "./attendant-listening.svg?raw";
import speakingSvg from "./attendant-speaking.svg?raw";
import thinkingSvg from "./attendant-thinking.svg?raw";

export type AttendantVisualState = "idle" | "listening" | "thinking" | "speaking" | "celebrating" | "error";

interface AttendantAvatarProps {
    state: AttendantVisualState;
    size?: number | string;
    accentColor?: string;
    className?: string;
}

type AttendantCssVars = React.CSSProperties & {
    "--att-body"?: string;
    "--att-edge"?: string;
    "--att-face"?: string;
    "--att-accent"?: string;
    "--att-cheek"?: string;
    "--att-highlight"?: string;
};

const svgByState: Record<AttendantVisualState, string> = {
    idle: idleSvg,
    listening: listeningSvg,
    thinking: thinkingSvg,
    speaking: speakingSvg,
    celebrating: celebratingSvg,
    error: errorSvg,
};

export const AttendantAvatar: React.FC<AttendantAvatarProps> = ({
    state,
    size = 44,
    accentColor,
    className,
}) => {
    const theme = useTheme();
    const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const svgMarkup = svgByState[state];

    const cssVars: AttendantCssVars = {
        "--att-body": theme.palette.mode === "dark" ? "#6B5340" : "#54402F",
        "--att-edge": theme.palette.mode === "dark" ? "#8A7259" : "#362719",
        "--att-face": "#241A12",
        "--att-accent": accentColor || theme.palette.primary.main,
        "--att-cheek": "#D98E66",
        "--att-highlight": "#FFFDF8",
    };

    const svgVarStyle = [
        `--att-body: ${cssVars["--att-body"]}`,
        `--att-edge: ${cssVars["--att-edge"]}`,
        `--att-face: ${cssVars["--att-face"]}`,
        `--att-accent: ${cssVars["--att-accent"]}`,
        `--att-cheek: ${cssVars["--att-cheek"]}`,
        `--att-highlight: ${cssVars["--att-highlight"]}`,
    ].join("; ");

    const themedSvgMarkup = svgMarkup.replace("<svg ", `<svg style="${svgVarStyle}" `);

    const attendantKeyframes = (
        <GlobalStyles styles={`
        @keyframes att-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-2px)}}
        @keyframes att-blink{0%,92%,100%{transform:scaleY(1)}95%,97%{transform:scaleY(0.08)}}
        @keyframes att-rock{0%,100%{transform:rotate(-3deg)}50%{transform:rotate(3deg)}}
        @keyframes att-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-1.5px)}}
        @keyframes att-cele{0%,100%{transform:translateY(0) rotate(0)}25%{transform:translateY(-3px) rotate(-6deg)}75%{transform:translateY(-2px) rotate(6deg)}}
        @keyframes att-shake{0%,100%{transform:translateX(0) rotate(0)}25%{transform:translateX(-1.2px) rotate(-2deg)}75%{transform:translateX(1.2px) rotate(2deg)}}
        @keyframes att-glow{0%,100%{opacity:0.28}50%{opacity:0.55}}
        @keyframes att-spark{0%{opacity:0;transform:scale(0.4) translateY(0)}40%{opacity:1;transform:scale(1) translateY(-3px)}100%{opacity:0;transform:scale(1.4) translateY(-8px)}}
        @keyframes att-talk-a{0%,100%{transform:scaleY(1)}50%{transform:scaleY(1.6)}}
        @keyframes att-talk-b{0%,100%{transform:scaleY(1)}50%{transform:scaleY(2.1)}}
        @keyframes att-dot{0%,100%{opacity:0.25}50%{opacity:1}}
        @keyframes att-twitch{0%,55%,100%{transform:rotate(0)}60%{transform:rotate(-9deg)}66%{transform:rotate(5deg)}72%{transform:rotate(-3deg)}78%{transform:rotate(0)}}
        @keyframes att-perk{0%,100%{transform:rotate(0)}50%{transform:rotate(-8deg)}}
        @keyframes att-pulse{0%,100%{opacity:0.45;transform:scale(1)}50%{opacity:1;transform:scale(1.15)}}
        `} />
    );

    return (
        <>
            {attendantKeyframes}
            <Box
                aria-hidden="true"
                className={className}
                data-attendant-state={state}
                sx={{
                    width: size,
                    height: size,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 0,
                    flexShrink: 0,
                    pointerEvents: "none",
                    "& .att-svg": {
                        width: "100%",
                        height: "100%",
                        display: "block",
                        overflow: "visible",
                    },
                    ...(prefersReducedMotion
                        ? {
                            "& .att-svg *": {
                                animation: "none !important",
                                transition: "none !important",
                            },
                        }
                        : {}),
                }}
                dangerouslySetInnerHTML={{ __html: themedSvgMarkup }}
            />
        </>
    );
};